import axiosInstance from "./apiinterseptor";
import { toast } from "react-hot-toast"

export function getApproachApiErrorMessage(error: any): string {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error?.message ||
    "Unknown error"
  );
}

export function isBlockedPhoneError(error: any): boolean {
  const message = getApproachApiErrorMessage(error);
  return /blocked by admin/i.test(message);
}

/** Same blocked check used before Schedule → Add Candidate (throws 403 if blocked). */
export const checkApproachPhoneBlocked = async (phone: string) => {
  const digits = String(phone ?? "").replace(/\D/g, "");
  const normalized = digits.length >= 10 ? digits.slice(-10) : digits;
  const response = await axiosInstance.get(`/approaches/check-blocked-phone`, {
    params: { phone: normalized },
  });
  return response.data;
};

// Get approaches with query params
export const getApproaches = async (params: {
  search?: string;
  recruiterId?: string;
  callStatus?: string;
  appStatus?: string;
  fromDate?: string;
  toDate?: string;
  source?: string;
  jobId?: string;
  hasWebhookEvents?: string;
  whatsappOutcome?: string;
  cursor?: string;
  limit?: number;
} = {}) => {
  try {
    const query = new URLSearchParams(params as any).toString();
    const response = await axiosInstance.get(`/approaches${query ? `?${query}` : ""}`);
    // The backend returns { data: { approaches }, ... }
    // So return { data: approaches, ...otherMeta }
    return {
      ...response.data,
      data: response.data.data?.approaches || [],
    };
  } catch (error: any) {
    toast.error(`Failed to fetch approaches: ${error.response?.data?.message || 'Unknown error'}`);
    throw error;
  }
};

export const getCompanyApproaches = async (params: {
  search?: string;
  recruiterId?: string;
  callStatus?: string;
  fromDate?: string;
  toDate?: string;
  source?: string;
  page?: number;
  limit?: number;
} = {}) => {
  try {
    const query = new URLSearchParams(params as any).toString();
    const response = await axiosInstance.get(`/approaches/companyApproaches${query ? `?${query}` : ""}`);
    // The backend returns { data: { approaches }, ... }
    // So return { data: approaches, ...otherMeta }
    return {
      ...response.data,
      data: response.data.data?.approaches || [],
    };
  } catch (error: any) {
    toast.error(`Failed to fetch approaches: ${error.response?.data?.message || 'Unknown error'}`);
    throw error;
  }
};
// Export approaches in batches (cursor-based; use with pagination loop on client)
export interface ExportApproachesResponse {
  approaches: Record<string, unknown>[];
  hasNextPage: boolean;
  nextCursor: string | null;
  totalResults: number;
}

export const exportApproaches = async (params: {
  search?: string;
  recruiterId?: string;
  callStatus?: string;
  fromDate?: string;
  toDate?: string;
  source?: string;
  jobId?: string;
  whatsappOutcome?: string;
  hasWebhookEvents?: string;
  cursor?: string;
  limit?: number;
} = {}): Promise<ExportApproachesResponse> => {
  try {
    const query = new URLSearchParams(
      Object.entries(params)
        .filter(([, value]) => value !== undefined && value !== null && value !== "")
        .map(([key, value]) => [key, String(value)])
    ).toString();
    const response = await axiosInstance.get(`/approaches/export${query ? `?${query}` : ""}`);
    return response.data as ExportApproachesResponse;
  } catch (error: any) {
    toast.error(`Failed to export approaches: ${error.response?.data?.message || 'Unknown error'}`);
    throw error;
  }
};

// Export company approaches
export const exportCompanyApproaches = async (params: {
  search?: string;
  recruiterId?: string;
  callStatus?: string;
  fromDate?: string;
  toDate?: string;
  source?: string;
} = {}) => {
  try {
    const query = new URLSearchParams(params as any).toString();
    const response = await axiosInstance.get(`/approaches/companyApproaches/export${query ? `?${query}` : ""}`);
    return response.data;
    
  } catch (error: any) {
    toast.error(`Failed to fetch approaches: ${error.response?.data?.message || 'Unknown error'}`);
    throw error;
  }
};
// Create a new approach  
export const createApproach = async (data: {
  name: string;
  phone: string;
  email: string;
  qualifications: string;
  source: string;
  callStatus: string;
  callBackTime?: string;
  jobId: string;
  applicationId?: string;
}) => {
  try {
    const response = await axiosInstance.post("/approaches/createApproach", data);
    toast.success("Approach created successfully");
    return response.data;
  } catch (error: any) {
    if (!isBlockedPhoneError(error)) {
      toast.error(
        `Failed to create approach: ${getApproachApiErrorMessage(error)}`
      );
    }
    throw error;
  }
};

// Create a new company approach
export const createCompanyApproach = async (data: {
  name: string;
  phone: string;
  email: string;
  source: string;
  callStatus: string;
  callBackTime?: string;
}) => {
  try {
    const response = await axiosInstance.post("/approaches/createCompanyApproach", data);
    toast.success("Company approach created successfully");
    return response.data;
  } catch (error: any) {
    toast.error(`Failed to create company approach: ${error.response?.data?.message || 'Unknown error'}`);
    throw error;
  }
};
// create a new approch from hr job assignments
export const createApproachFromAssignments = async (data: {
  name: string;
  phone: string;
  email: string;
  qualifications: string;
  source: string;
  callStatus: string;
  callBackTime?: string;
  jobId?: string
}) => {

  console.log(data)
  try {
    const response = await axiosInstance.post("/approaches/createApproachFromAssignments", data);
    
    toast.success("Approach created successfully");
    return response.data;
  } catch (error: any) {
    if (!isBlockedPhoneError(error)) {
      toast.error(
        `Failed to create approach: ${getApproachApiErrorMessage(error)}`
      );
    }
    throw error;
  }
};
  

// Update an approach by ID
export const updateApproach = async (
  id: string,
  data: Partial<{
    name: string;
    phone: string;
    email: string;
    qualifications: string;
    source: string;
    callStatus: string;
    callBackTime: string;
    applicationId: string;
    remarks: string;
  }>
) => {
  try {
    const response = await axiosInstance.patch(`/approaches/${id}`, data);
    toast.success("Approach updated successfully");
    return response.data;
  } catch (error: any) {
    toast.error(`Failed to update approach: ${error.response?.data?.message || 'Unknown error'}`);
    throw error;
  }
};

// Update a company approach by ID
export const updateCompanyApproach = async (
  id: string,
  data: Partial<{
    callStatus: string;
    callBackTime: string;
    remarks: string;
  }>
) => {
  try {
    const response = await axiosInstance.patch(`/approaches/companyApproach/${id}`, data);
    toast.success("Company approach updated successfully");
    return response.data;
  } catch (error: any) {
    toast.error(`Failed to update company approach: ${error.response?.data?.message || 'Unknown error'}`);
    throw error;
  }
};

export type HotLeadJobRef = {
  _id?: string;
  title?: string;
  companyId?: { name?: string } | null;
} | null;

export type HotLeadRecord = {
  _id: string;
  approachId: string;
  name: string;
  phone: string;
  email?: string;
  qualifications?: string;
  city?: string;
  source: string;
  remarks?: string;
  callStatus: string;
  jobId?: HotLeadJobRef;
  profileCreatedByUserId?: { username?: string; email?: string };
  lastRecordedByUserId?: { username?: string; email?: string };
  lastSheetActionByUserId?: { username?: string; email?: string };
  lastSheetActionAt?: string;
  nrRnrSheetActionCount?: number;
  hiddenUntil?: string;
  dismissedPermanently?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export const getHotLeads = async (params: { page?: number; limit?: number; jobId?: string } = {}) => {
  try {
    const q = new URLSearchParams();
    if (params.page != null) q.set("page", String(params.page));
    if (params.limit != null) q.set("limit", String(params.limit));
    if (params.jobId) q.set("jobId", String(params.jobId));
    const query = q.toString();
    const response = await axiosInstance.get(`/approaches/hot-leads${query ? `?${query}` : ""}`);
    return {
      ...response.data,
      data: response.data.data?.hotLeads ?? [],
    };
  } catch (error: any) {
    toast.error(error.response?.data?.message || "Failed to load hot leads");
    throw error;
  }
};

/** Must match backend HOT_LEAD_SHEET_UPDATE_STATUSES (Schedule = DB value). */
export const HOT_LEAD_SHEET_STATUS_OPTIONS = [
  { value: "Not Reachable", label: "Not Reachable" },
  { value: "Ringing Not Reachable", label: "Ringing Not Reachable" },
  { value: "Interested", label: "Interested" },
  { value: "Not Interested", label: "Not Interested" },
  { value: "Call Back", label: "Call Back" },
  { value: "Schedule", label: "Scheduled" },
  { value: "Salary Issue", label: "Salary Issue" },
  { value: "Incorrect Number", label: "Incorrect Number" },
  { value: "DND", label: "DND" },
] as const;

export const updateHotLeadStatus = async (hotLeadId: string, callStatus: string) => {
  try {
    const response = await axiosInstance.patch(`/approaches/hot-leads/${hotLeadId}`, {
      callStatus,
    });
    if (response.data?.message) {
      toast.success(response.data.message);
    } else {
      toast.success("Status updated");
    }
    return response.data;
  } catch (error: any) {
    toast.error(error.response?.data?.message || "Failed to update status");
    throw error;
  }
};